import { Icons } from "../../constants/icon-library";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { fetchSettingDetailsByUserId } from "../../features/settings/settingsSlice";
import useCreateMeeting from "../Meetings/CreateMeeting.utils";
import FileRow from "../Meetings/FileRow";
import {
  createAllDummyMeetings,
  getAllUserMeetings,
} from "../../features/meetings/meetingsSlice";
import Loader from "../../components/common/Loader";

const CreateMeeting = () => {
  const {
    formik,
    uploadedFiles,
    doneFiles,
    doneCounts,
    anyUploading,
    loading,
    handleRemove,
    getRootProps,
    getInputProps,
    isDragActive,
    navigate,
  } = useCreateMeeting({ isOnboarding: true });

  const dispatch = useDispatch();
  const [dummyLoading, setDummyLoading] = useState(false);
  const { meetings } = useSelector((state) => state.meetings);

  useEffect(() => {
    dispatch(getAllUserMeetings());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSettingDetailsByUserId());
  }, [dispatch]);

  const handleRedirect = async () => {
    try {
      setDummyLoading(true);
      await dispatch(createAllDummyMeetings());

      const interval = setInterval(async () => {
        try {
          const result = await dispatch(getAllUserMeetings()).unwrap();

          const meetingsList = Array.isArray(result)
            ? result
            : result?.meetings || [];

          const hasProcessingMeeting = meetingsList.some(
            (meeting) => meeting.status === "PROCESSING",
          );

          if (!hasProcessingMeeting) {
            clearInterval(interval);

            navigate("/onboarding/meetings");
          }
        } catch (error) {
          clearInterval(interval);
          setDummyLoading(false);
        }
      }, 10000);
    } catch (error) {
      console.error(error);
      setDummyLoading(false);
    }
  };
  
  const isMeetingProcessing = meetings?.some(
    (meeting) =>
      meeting.status === "PROCESSING" || meeting.status === "PROCESSING_TASKS",
  );

  if (dummyLoading || isMeetingProcessing) {
    return (
      <Loader
        title="Preparing your meetings..."
        message="Please wait while we import sample transcripts."
      />
    );
  }
  const hasMeetings = Array.isArray(meetings) && meetings.length > 0;
  return (
    <div className="container py-5 mt-4" style={{ maxWidth: "1400px" }}>
      <div className="row">
        <div className="p-4 border-bottom bg-body d-flex align-items-center justify-content-center">
          <h3 className="mb-0">Create New Meeting</h3>
        </div>
        <div className="card border p-0">
          <div className="card-body">
            <form onSubmit={formik.handleSubmit}>
              <div className="row align-items-stretch">
                <div className="col-md-6 d-flex">
                  <div
                    className="mb-4 d-flex flex-column w-100"
                    style={{ height: "50vh" }}
                  >
                    <div className="d-flex justify-content-between">
                      <div>
                        {" "}
                        <label className="fw-bold mb-2">Upload File</label>
                      </div>{" "}
                      <div>
                        {" "}
                        {doneFiles.length > 0 && (
                          <p className="text-muted small mb-0 mt-1">
                            {doneCounts.recordings > 0 &&
                              `${doneCounts.recordings} audio · `}
                            {doneCounts.documents > 0 &&
                              `${doneCounts.documents} document${doneCounts.documents !== 1 ? "s" : ""} · `}
                            {doneFiles.length} file
                            {doneFiles.length !== 1 ? "s" : ""} ready
                            {anyUploading ? " · uploading…" : ""}
                          </p>
                        )}
                      </div>
                    </div>

                    <div
                      {...getRootProps()}
                      className={`border rounded p-3 mb-1 text-center ${
                        isDragActive
                          ? "border-primary bg-primary bg-opacity-10"
                          : "border-dashed"
                      }`}
                      style={{
                        cursor: "pointer",
                        transition: "all 0.2s",
                        flexShrink: uploadedFiles.length > 0 ? 0 : undefined,
                        flex: uploadedFiles.length > 0 ? undefined : 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <input {...getInputProps()} />
                      <div className="mb-2" style={{ fontSize: 32 }}>
                        {isDragActive ? "📂" : "⬆️"}
                      </div>
                      <p className="mb-1 fw-semibold">
                        {isDragActive
                          ? "Release to upload"
                          : "Drop files here, or click to browse"}
                      </p>
                      <p className="text-muted small mb-3">
                        MP3, M4A, WAV · PDF, DOC, DOCX · Images
                      </p>
                      <button type="button" className="btn btn-primary btn-sm">
                        <i className={`wb ${Icons("add")} me-1`} />
                        Browse Files
                      </button>
                    </div>

                    {uploadedFiles.length > 0 && (
                      <div
                        className="border rounded p-2 mb-1"
                        style={{ flex: 1, minHeight: 0, overflowY: "auto" }}
                      >
                        {uploadedFiles.map((item) => (
                          <FileRow
                            key={item.id}
                            item={item}
                            onRemove={handleRemove}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-md-6 d-flex">
                  <div
                    className="mb-4 d-flex flex-column w-100"
                    style={{ height: "50vh" }}
                  >
                    <label className="fw-bold mb-2">Meeting Transcript</label>
                    <textarea
                      name="rawText"
                      className={`form-control flex-fill ${
                        formik.touched.rawText && formik.errors.rawText
                          ? "is-invalid"
                          : ""
                      }`}
                      value={formik.values.rawText}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Type or paste your meeting transcript here…"
                      style={{ resize: "none" }}
                    />

                    {formik.touched.rawText && formik.errors.rawText && (
                      <div className="invalid-feedback d-block">
                        {formik.errors.rawText}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {formik.submitCount > 0 &&
                doneFiles.length === 0 &&
                !formik.values.rawText.trim() && (
                  <div className="alert alert-warning py-2 mb-3 d-flex align-items-center  text-black gap-2">
                    <i className={`wb ${Icons("warning")}`} />
                    <span>
                      Please upload at least one file or transcription text.
                    </span>
                  </div>
                )}

              <div className="mb-3">
                <label className="fw-bold mb-2">
                  Meeting Name <span className="text-danger">*</span>
                </label>

                <input
                  type="text"
                  name="name"
                  className={`form-control ${
                    formik.touched.name && formik.errors.name
                      ? "is-invalid"
                      : ""
                  }`}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter meeting name"
                />

                {formik.touched.name && formik.errors.name && (
                  <div className="invalid-feedback">{formik.errors.name}</div>
                )}
              </div>
              {/* Buttons */}
              <div className="d-flex justify-content-between align-items-center mt-4"></div>
            </form>
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-between align-items-center mt-4 pt-4 border-top">
        <button className="btn btn-primary px-4" onClick={() => navigate(-1)}>
          ← Previous
        </button>

        <div>
          <div className="d-flex align-items-center gap-3">
            {!hasMeetings ? (
              <button
                className="btn btn-link text-muted"
                onClick={handleRedirect}
                style={{ fontSize: "1.1rem" }}
              >
                Skip & continue with dummy transcript
              </button>
            ) : (
              <button
                className="btn btn-link text-muted"
                onClick={() => navigate("/onboarding/meetings")}
                style={{ fontSize: "1.1rem" }}
              >
                Skip now
              </button>
            )}
            <button
              type="button"
              className="btn btn-primary btn-sm d-flex align-items-center gap-2"
              disabled={loading || anyUploading}
              onClick={formik.submitForm}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" />
                  Submitting…
                </>
              ) : anyUploading ? (
                <>
                  <span className="spinner-border spinner-border-sm" />
                  Uploading files…
                </>
              ) : (
                <>
                  <i className={`wb ${Icons("light")} `} />
                  Create Meeting
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateMeeting;
